<template>
  <!--
    ArtifactDetail SECTION — Patient Flag artifact, "Data Source" tab.

    WHAT: The join graph that resolves which patients qualify for the flag,
      evaluated as-of-now into PATIENT_FLAG_STATE. Must emit BENE_MBI_ID; VALUE is
      optional (required for categorical flags); VALID_FROM/VALID_THRU gate the
      window. No date spine or population needed — typically a single intervention
      DA. Thin wrapper over the shared JoinGraphSection.

    CONTRACT (props { record, artifact, context }):
      READS  record.studio_config (steps + primary_key_columns; note flags store
             studio_config at the TOP LEVEL of the record, not under
             composable_config), context.attributes.
      EMITS  patch({ studio_config: { steps, primary_key_columns } }) via onStudio
             (primary_key_columns defaults to ['BENE_MBI_ID']); refresh() on
             @attribute-saved.

    NOTES:
      - sql-url is /api/impact_analytics/patient-flags/{id}/studio-sql/ (POST).
      - selfChrome: true in the registry — StudioJoinBuilder draws its own card.
      - hydrating guard lives inside the wrapped JoinGraphSection.
  -->
  <div>
    <p class="text-[11px] text-gray-500 mb-3 leading-relaxed">
      Evaluated <span class="font-medium">as-of-now</span> into
      <span class="font-mono">PATIENT_FLAG_STATE</span>. The query must resolve
      <span class="font-mono">BENE_MBI_ID</span>; <span class="font-mono">VALUE</span> optional
      (categorical flags require it), <span class="font-mono">VALID_FROM/VALID_THRU</span> gate the window.
      No date spine or population needed.
    </p>
    <JoinGraphSection
      :value="studioConfig"
      :attributes="context.sources || context.attributes || []"
      :sql-url="`/api/impact_analytics/patient-flags/${record.id}/studio-sql/`"
      config-tab-label="Flag"
      consumer-label="flag"
      @update:value="onStudio"
      @attribute-saved="$emit('refresh')"
    >
      <template #config>
        <div class="text-[11px] text-gray-500 p-3">
          Presentation (label, color, priority, categorical map) lives in the Presentation section.
          Build the flag's condition as a join graph here — typically a single intervention DA
          emitting qualifying patients.
        </div>
      </template>
    </JoinGraphSection>
  </div>
</template>

<script>
import JoinGraphSection from './JoinGraphSection.vue';
import { withoutSteps } from '@/utils/consumerSteps';

export default {
  name: 'FlagDataSourceSection',
  components: { JoinGraphSection },
  props: {
    record: { type: Object, required: true },
    artifact: { type: Object, required: true },
    context: { type: Object, default: () => ({}) },
  },
  emits: ['patch', 'refresh'],
  computed: {
    studioConfig() { return this.record.studio_config || { steps: [], primary_key_columns: ['BENE_MBI_ID'] }; },
  },
  methods: {
    onStudio(sc) {
      if (sc.workflow_id) {
        this.$emit('patch', { studio_config: {
          ...withoutSteps(this.studioConfig),
          workflow_id: sc.workflow_id,
          primary_key_columns: sc.primary_key_columns || ['BENE_MBI_ID'],
        } });
        return;
      }
      this.$emit('patch', { studio_config: {
        steps: sc.steps,
        primary_key_columns: sc.primary_key_columns || ['BENE_MBI_ID'],
      } });
    },
  },
};
</script>
